import { Checkbox, CommandBar, Dropdown, List, MessageBar, MessageBarType, ProgressIndicator, Stack } from '@fluentui/react';
import * as React from 'react';
import { sendApiRequest, styles } from '../lib/helper';
import { useAuthenticationContext } from '../providers/authenticationprovider';
import { IAccreditor } from '../types/IAccreditor';
import { ICourse } from '../types/ICourse';


const AccreditorCourseSettings: React.FunctionComponent<{ course: ICourse }> = ({ course }) => {

    const authContext = useAuthenticationContext();

    const [error, setError] = React.useState<string | undefined>();
    const [loadingAllStudents, setLoadingAllStudents] = React.useState<boolean>(true);
    const [loadingSelectedStudents, setLoadingSelectedStudents] = React.useState<boolean>(false);
    const [accreditors, setAccreditors] = React.useState<IAccreditor[]>();
    const [selectedAccreditor, setSelectedAccreditor] = React.useState<IAccreditor>();
    const [allStudentsInCourse, setAllStudentsInCourse] = React.useState<string[]>();
    const [selectedStudentsInCourse, setSelectedStudentsInCourse] = React.useState<string[]>();
    const [saving, setSaving] = React.useState<boolean>(false);
    const [successMessage, setSuccessMessage] = React.useState<string>();

    React.useEffect(() => {
        setSuccessMessage(undefined);
        if(authContext.Result?.KeplerAuthStatus?.isKeplerAdministrator)
        {
            if(course)
            {
                sendApiRequest("/api/sql/getListOfAccreditors", "GET").then(accreditors => {                                  
                    setAccreditors(accreditors);
                }).catch(err => {
                    console.error(err);
                    setError("Error getting list of accreditors")
                }).finally(() => {
                    setLoadingAllStudents(false);
                })
            }
            else
            {
                setError("Invalid course parameter")
                setLoadingAllStudents(false);
            }
        }
        else{
            setError("You are not allowed to view this page")
            setLoadingAllStudents(false);
        }
    }, [course, authContext])

    React.useEffect(() => {
        setSuccessMessage(undefined);
        if(course && selectedAccreditor && !allStudentsInCourse)
        {            
            setLoadingAllStudents(true)
            setAllStudentsInCourse(undefined);
            sendApiRequest(`/api/blobStorage/getStudentsInCourseFromBlobStorage?coursePath=${course.BlobFolderName}`, "GET").then(students => {                                  
                setAllStudentsInCourse(students.sort((a: string, b: string) => a > b ? 1: -1));
            }).catch(err => {
                console.error(err);
                setError("Error getting list of students")
            }).finally(() => {
                setLoadingAllStudents(false)
            });
        }
    }, [selectedAccreditor, course, allStudentsInCourse])

    React.useEffect(() => {
        setSuccessMessage(undefined);
        if(course && selectedAccreditor)
        {            
            setLoadingSelectedStudents(true)
            setSelectedStudentsInCourse(undefined);
            sendApiRequest(`/api/sql/getListOfStudentsForAccreditorAndCourse?Course=${course.SIS || course.Course}&AccreditorId=${selectedAccreditor.AccreditorId}`, "GET").then((students: any[]) => {                                  
                setSelectedStudentsInCourse(students.map(s => s.StudentName));
            }).catch(err => {
                console.error(err);
                setError("Error getting list of students")
            }).finally(() => {
                setLoadingSelectedStudents(false)
            });
        }
    }, [selectedAccreditor, course])

    return <div style={{paddingLeft: 20}}>
        {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
        
        {accreditors && <Dropdown 
            options={accreditors.map(a => ({key: a.AccreditorId, text: a.AccreditorName}))}
            label="Accreditor"
            selectedKey={selectedAccreditor?.AccreditorId}
            onChange={(_ev, option) => {
                if(option?.key)
                {
                    const accreditorSelected = accreditors.find(a => a.AccreditorId === option.key);
                    if(accreditorSelected)
                    {
                        setSelectedAccreditor(accreditorSelected);
                    }
                }
            }} />}
        {(loadingAllStudents || loadingSelectedStudents) && <ProgressIndicator />}
        {selectedAccreditor && !loadingAllStudents && allStudentsInCourse && <>
        <Stack>
            <div>Choose which students you want to include for the accreditor</div>
            <CommandBar styles={{ root: styles.commandBarRoot }} items={[{
                key: "selectAll",
                text: selectedStudentsInCourse && selectedStudentsInCourse.length === allStudentsInCourse.length ? "Unselect All" : "Select All",
                buttonStyles: { root: styles.commandBarButtonRoot, rootDisabled: styles.commandBarButtonRoot, rootChecked: styles.commandBarButtonRootChecked, iconChecked: styles.commandBarButtonRootChecked },
                onClick: () => {
                    if(selectedStudentsInCourse && selectedStudentsInCourse.length === allStudentsInCourse.length)
                    {
                        setSelectedStudentsInCourse([]);
                    }
                    else
                    {
                        setSelectedStudentsInCourse([...allStudentsInCourse]);
                    }
                }
            }, {
                key: "save",
                text: "Save",
                disabled: saving,
                buttonStyles: { root: styles.commandBarButtonRoot, rootDisabled: styles.commandBarButtonRoot, rootChecked: styles.commandBarButtonRootChecked, iconChecked: styles.commandBarButtonRootChecked },
                onClick: () => {
                    setSaving(true);
                    sendApiRequest(`/api/sql/updateListOfStudentsForAccreditorAndCourse?Course=${course.SIS || course.Course}&AccreditorId=${selectedAccreditor.AccreditorId}`, "POST", JSON.stringify({Students: selectedStudentsInCourse})).then(students => {                                  
                        setSaving(false);
                        setSuccessMessage("Successfully saved students");
                    }).catch(err => {
                        console.error(err);
                        setError("Error saving list of students")
                    }).finally(() => {
                        setSaving(false)
                    });
                }
            }]} />
            {successMessage && <MessageBar messageBarType={MessageBarType.success}>{successMessage}</MessageBar>}
            <List items={[...allStudentsInCourse]} onRenderCell={(item, index) => {
                return <Checkbox styles={{root: {marginTop: 10}}} key={`student-cb-${index}`} label={item} checked={selectedStudentsInCourse?.find(s => item === s) ? true : false} onChange={(_ev, checked) => {
                    let studentsChecked = [...(selectedStudentsInCourse || [])];
                    if(checked)
                    {
                        studentsChecked.push(item!);
                    }
                    else
                    {
                        studentsChecked = studentsChecked.filter(s => s !== item);
                    }
                    setSelectedStudentsInCourse(studentsChecked);
                }} />
            }} />
        </Stack>
        </>}
    </div>;
};

export default AccreditorCourseSettings;